import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../css/Home.css';
import { domain } from './Domain';
import { NavLink } from 'react-router-dom';
import Footer from './Footer';
import ProductCard from './ProductCard'
import { ProductCardSkeleton } from './ProductCard';
import NoDataComponent from './NoData';
import { useNavContext } from './NavContext';
import CartButton from './CartButton';
import { useLoginContext } from './LoginContext';

const PAGE_SIZE = 30;
const SCROLL_ROOT_MARGIN = '320px';

const Welcome = () => {
    const auth = useLoginContext();
    const [products, setProducts] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const sentinelRef = useRef(null);
    const loadingMoreRef = useRef(false);

    const getHeaders = useCallback(() => {
        const headers = {};
        if (auth.userInfo.token !== null) {
            headers.Authorization = `Token ${auth.userInfo.token}`;
        }
        return headers;
    }, [auth.userInfo.token]);

    const fetchPage = useCallback(
        async (offset, { signal } = {}) => {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: String(offset),
            });
            const res = await fetch(`${domain}/products?${params}`, {
                signal,
                headers: getHeaders(),
            });
            if (!res.ok) {
                throw new Error(res.statusText || 'Failed to load products');
            }
            const data = await res.json();
            const batch = Array.isArray(data.results) ? data.results : data;
            const count =
                typeof data.count === 'number'
                    ? data.count
                    : Array.isArray(batch)
                      ? batch.length
                      : 0;
            return { batch, count };
        },
        [getHeaders]
    );

    useEffect(() => {
        const abort = new AbortController();
        setLoading(true);
        setError(null);
        setProducts([]);
        setTotalCount(0);

        fetchPage(0, { signal: abort.signal })
            .then(({ batch, count }) => {
                setProducts(batch);
                setTotalCount(count);
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                setError(err.message);
            })
            .finally(() => setLoading(false));

        return () => abort.abort();
    }, [fetchPage]);

    const hasMore = products.length < totalCount;

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || !hasMore || loading) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setError(null);
        const offset = products.length;
        try {
            const { batch, count } = await fetchPage(offset);
            setTotalCount(count);
            setProducts((prev) => {
                const seen = new Set(prev.map((p) => p.id));
                const merged = [...prev];
                for (const p of batch) {
                    if (!seen.has(p.id)) {
                        seen.add(p.id);
                        merged.push(p);
                    }
                }
                return merged;
            });
        } catch (err) {
            if (err.name !== 'AbortError') {
                setError(err.message);
            }
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [fetchPage, hasMore, loading, products.length]);

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || loading || !hasMore) return;

        const scrollRoot =
            node.closest('.scrollable-content') ||
            node.closest('.home-container') ||
            null;

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                loadMore();
            },
            {
                root: scrollRoot,
                rootMargin: SCROLL_ROOT_MARGIN,
                threshold: 0,
            }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [loading, hasMore, loadMore, products.length]);

    return (
        <div className="welcome d-flex flex-column">
            <h4>Take time to view our products</h4>
            {error && (
                <p className="text-danger small" role="alert">
                    {error}
                </p>
            )}
            <div className="product-list d-flex gap-3 flex-wrap align-items-stretch">
                {loading ? (
                    <React.Fragment>
                        <ProductCardSkeleton />
                        <ProductCardSkeleton />
                        <ProductCardSkeleton />
                        <ProductCardSkeleton />
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        {products.length === 0 ? (
                            <NoDataComponent showNoData={true} />
                        ) : (
                            <>
                                {products.map((product) => (
                                    <ProductCard
                                        key={product.id}
                                        id={product.id}
                                        name={product.product_name}
                                        partNumber={product.part_number}
                                        stockAmount={product.qty}
                                        price={product.selling_price}
                                        image={domain + product.image}
                                    />
                                ))}
                                {hasMore && (
                                    <div
                                        ref={sentinelRef}
                                        className="w-100"
                                        style={{ minHeight: 1 }}
                                        aria-hidden
                                    />
                                )}
                            </>
                        )}
                    </React.Fragment>
                )}
            </div>
            {loadingMore && (
                <div
                    className="d-flex gap-3 flex-wrap align-items-stretch mt-2"
                    aria-busy="true"
                >
                    <ProductCardSkeleton />
                    <ProductCardSkeleton />
                </div>
            )}
        </div>
    );
};

const navLinkClass = ({ isActive }) =>
    `category-nav-link${isActive ? ' category-nav-link--active' : ''}`;

export const CategoryLinks = () => {
    const { mainNav } = useNavContext();
    const categories = Array.isArray(mainNav) ? mainNav : [];
    const [filter, setFilter] = useState('');

    const q = filter.trim().toLowerCase();
    const matches = (name) =>
        !q || (name && String(name).toLowerCase().includes(q));

    const featured = categories.filter((c) => c.header && matches(c.name));
    const more = categories.filter((c) => !c.header && matches(c.name));
    const showEmptyFilter =
        q && featured.length === 0 && more.length === 0;

    return (
        <nav
            className="category-sidebar"
            aria-label="Product categories"
        >
            <div className="category-sidebar-intro">
                <h2 className="category-sidebar-title">Categories</h2>
                <p className="category-sidebar-desc">
                    Browse by type or view the full catalog. Use the filter to
                    narrow the list.
                </p>
            </div>
            <label className="visually-hidden" htmlFor="category-filter">
                Filter category list
            </label>
            <input
                id="category-filter"
                type="search"
                className="category-sidebar-search form-control form-control-sm"
                placeholder="Filter categories…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                autoComplete="off"
            />
            {showEmptyFilter ? (
                <p className="category-sidebar-empty small text-muted mb-0">
                    No categories match “{filter.trim()}”.
                </p>
            ) : (
                <div className="category-sidebar-sections">
                    <div className="category-sidebar-section">
                        <h3 className="category-sidebar-subtitle d-none d-md-block">
                            Catalog
                        </h3>
                        <NavLink to="/" end className={navLinkClass}>
                            All products
                        </NavLink>
                    </div>
                    {featured.length > 0 && (
                        <div className="category-sidebar-section">
                            <h3 className="category-sidebar-subtitle">
                                Featured
                            </h3>
                            <div className="category-sidebar-link-stack">
                                {featured.map((item) => (
                                    <NavLink
                                        key={item.id}
                                        to={`/products/${item.slug}`}
                                        className={navLinkClass}
                                    >
                                        {item.name}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    )}
                    {more.length > 0 && (
                        <div className="category-sidebar-section">
                            <h3 className="category-sidebar-subtitle">
                                More categories
                            </h3>
                            <div className="category-sidebar-link-stack">
                                {more.map((item) => (
                                    <NavLink
                                        key={item.id}
                                        to={`/products/${item.slug}`}
                                        className={navLinkClass}
                                    >
                                        {item.name}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </nav>
    );
};

export const ComponentLayOut = ({ componentName }) => {
    return (
        <>
            <div className="home-container">
                <div className="side-panel">
                    <CategoryLinks />
                </div>
                <div className="scrollable-content">{componentName}</div>
            </div>
            <CartButton />
            <Footer />
        </>
    );
};

const Home = () => {
    return <ComponentLayOut componentName={<Welcome />} />;
};

export default Home;
